import { expect, html, nextFrame } from '@open-wc/testing';
import { createFixture } from '@patternfly/pfe-tools/test/create-fixture.js';
import { setViewport, sendKeys } from '@web/test-runner-commands';

import { allUpdates } from '@patternfly/pfe-tools/test/utils.js';

import { PfV5BackToTop } from '../pf-v5-back-to-top.js';
import { a11ySnapshot } from '@patternfly/pfe-tools/test/a11y-snapshot.js';

describe('<pf-v5-back-to-top>', function() {
  it('imperatively instantiates', function() {
    expect(document.createElement('pf-v5-back-to-top')).to.be.an.instanceof(PfV5BackToTop);
  });

  describe('simply instantiating', function() {
    let element: PfV5BackToTop;

    beforeEach(async function() {
      element = await createFixture<PfV5BackToTop>(html`<pf-v5-back-to-top></pf-v5-back-to-top>`);
    });

    it('should upgrade', function() {
      const klass = customElements.get('pf-v5-back-to-top');
      expect(element)
          .to.be.an.instanceOf(klass)
          .and
          .to.be.an.instanceOf(PfV5BackToTop);
    });
  });

  describe('when rendered in a viewport with a height smaller then content length', function() {
    let element: PfV5BackToTop;

    beforeEach(async function() {
      await setViewport({ width: 320, height: 640 });
      window.scrollTo({ top: 0, behavior: 'instant' });
      await nextFrame();
      const container = await createFixture<PfV5BackToTop>(html`
        <div id="top">
          <div style="height: 2000px;"></div>
          <pf-v5-back-to-top href="#top">Back to top</pf-v5-back-to-top>
        </div>
      `);
      element = container.querySelector('pf-v5-back-to-top')!;
      await allUpdates(element);
    });

    it('should be hidden on init', async function() {
      const snapshot = await a11ySnapshot();
      expect(snapshot).to.not.axContainRole('link');
    });

    it('should not be accessible', async function() {
      const snapshot = await a11ySnapshot();
      expect(snapshot).to.not.axContainName('Back to top');
    });

    describe('when scrolled 401px', function() {
      beforeEach(async function() {
        window.scrollTo({ top: 401, behavior: 'instant' });
        await nextFrame();
        await allUpdates(element);
      });

      it('should be visible', async function() {
        expect(await a11ySnapshot())
            .to.axContainQuery({ role: 'link', name: 'Back to top' });
      });

      it('should be accessible', async function() {
        await expect(element).to.be.accessible();
      });

      describe('pressing the tab key', function() {
        beforeEach(async function() {
          await sendKeys({ press: 'Tab' });
          await allUpdates(element);
          await nextFrame();
        });

        it('should focus the component', function() {
          expect(document.activeElement).to.equal(element);
        });
      });
    });

    describe('when the always visible property is true', function() {
      beforeEach(async function() {
        window.scrollTo({ top: 0, behavior: 'instant' });
        await nextFrame();
        element.alwaysVisible = true;
        await allUpdates(element);
      });

      it('should be visible', async function() {
        expect(await a11ySnapshot())
            .to.axContainQuery({ role: 'link', name: 'Back to top' });
      });

      it('should be accessible', async function() {
        await expect(element).to.be.accessible();
      });

      describe('pressing the tab key', function() {
        beforeEach(async function() {
          await sendKeys({ press: 'Tab' });
          await allUpdates(element);
          await nextFrame();
        });
        it('should focus the component', function() {
          expect(document.activeElement).to.equal(element);
        });
      });
    });

    describe('when the scroll distance is set to 1000', function() {
      beforeEach(async function() {
        element.scrollDistance = 1000;
        await allUpdates(element);
      });

      it('should be hidden', async function() {
        expect(await a11ySnapshot()).to.not.axContainRole('link');
      });

      describe('when scrolled 1001px', function() {
        beforeEach(async function() {
          window.scrollTo({ top: 1001, behavior: 'instant' });
          await nextFrame();
          await allUpdates(element);
        });

        it('should be visible', async function() {
          expect(await a11ySnapshot())
              .to.axContainQuery({ role: 'link', name: 'Back to top' });
        });
      });
    });
  });

  describe('when rendered in an element with an overflowed height', function() {
    let element: PfV5BackToTop;

    beforeEach(async function() {
      window.scrollTo({ top: 0, behavior: 'instant' });
      await nextFrame();
      const container = await createFixture<PfV5BackToTop>(html`
        <div id="top" style="height: 500px; overflow-y: scroll;">
          <div style="height: 2000px;"></div>
          <pf-v5-back-to-top href="#top" scrollable-selector="#top">Back to top</pf-v5-back-to-top>
        </div>
      `);
      element = container.querySelector('pf-v5-back-to-top')!;
      await allUpdates(element);
    });

    it('should be hidden on init', async function() {
      const snapshot = await a11ySnapshot({ selector: 'pf-v5-back-to-top' });
      expect(snapshot?.children).to.not.be.ok;
    });

    describe('when scrolled 401px', function() {
      beforeEach(async function() {
        const scrollableElement = document.querySelector('#top')!;
        scrollableElement.scrollTo({ top: 401, behavior: 'instant' });
        scrollableElement.dispatchEvent(new Event('scroll'));
        await nextFrame();
        await allUpdates(element);
      });

      it('should be visible', async function() {
        expect(await a11ySnapshot())
            .to.axContainQuery({ role: 'link', name: 'Back to top' });
      });
    });
  });

  describe('when no text is provided', function() {
    let element: PfV5BackToTop;

    describe('as a link', function() {
      beforeEach(async function() {
        await setViewport({ width: 320, height: 640 });
        window.scrollTo({ top: 0, behavior: 'instant' });
        await nextFrame();
        const container = await createFixture<PfV5BackToTop>(html`
          <div id="top">
            <div style="height: 2000px;"></div>
            <pf-v5-back-to-top href="#top"></pf-v5-back-to-top>
          </div>
        `);
        element = container.querySelector('pf-v5-back-to-top')!;
        await allUpdates(element);
      });

      describe('when scrolled', function() {
        beforeEach(async function() {
          window.scrollTo({ top: 401, behavior: 'instant' });
          await nextFrame();
          await allUpdates(element);
        });

        it('should have a label of "Back to top"', async function() {
          expect(await a11ySnapshot())
              .to.axContainQuery({ role: 'link', name: 'Back to top' });
        });
      });
    });

    describe('as a button', function() {
      beforeEach(async function() {
        await setViewport({ width: 320, height: 640 });
        window.scrollTo({ top: 0, behavior: 'instant' });
        await nextFrame();
        const container = await createFixture<PfV5BackToTop>(html`
          <div id="top">
            <div style="height: 2000px;"></div>
            <pf-v5-back-to-top></pf-v5-back-to-top>
          </div>
        `);
        element = container.querySelector('pf-v5-back-to-top')!;
        await allUpdates(element);
      });

      describe('when scrolled', function() {
        beforeEach(async function() {
          window.scrollTo({ top: 401, behavior: 'instant' });
          await nextFrame();
          await allUpdates(element);
        });

        it('should have a label of "Back to top"', async function() {
          expect(await a11ySnapshot())
              .to.axContainQuery({ role: 'button', name: 'Back to top' });
        });
      });
    });
  });

  describe('when a label is provided', function() {
    let element: PfV5BackToTop;

    describe('as a link', function() {
      beforeEach(async function() {
        await setViewport({ width: 320, height: 640 });
        window.scrollTo({ top: 0, behavior: 'instant' });
        await nextFrame();
        const container = await createFixture<PfV5BackToTop>(html`
          <div id="top">
            <div style="height: 2000px;"></div>
            <pf-v5-back-to-top href="#top" label="Return to top"></pf-v5-back-to-top>
          </div>
        `);
        element = container.querySelector('pf-v5-back-to-top')!;
        await allUpdates(element);
      });

      describe('when scrolled', function() {
        beforeEach(async function() {
          window.scrollTo({ top: 401, behavior: 'instant' });
          await nextFrame();
          await allUpdates(element);
        });

        it('should have a label of "Return to top"', async function() {
          expect(await a11ySnapshot())
              .to.axContainQuery({ role: 'link', name: 'Return to top' });
        });
      });
    });

    describe('as a button', function() {
      beforeEach(async function() {
        await setViewport({ width: 320, height: 640 });
        window.scrollTo({ top: 0, behavior: 'instant' });
        await nextFrame();
        const container = await createFixture<PfV5BackToTop>(html`
          <div id="top">
            <div style="height: 2000px;"></div>
            <pf-v5-back-to-top label="Return to top"></pf-v5-back-to-top>
          </div>
        `);
        element = container.querySelector('pf-v5-back-to-top')!;
        await allUpdates(element);
      });

      describe('when scrolled', function() {
        beforeEach(async function() {
          window.scrollTo({ top: 401, behavior: 'instant' });
          await nextFrame();
          await allUpdates(element);
        });

        it('should have a label of "Return to top"', async function() {
          expect(await a11ySnapshot())
              .to.axContainQuery({ role: 'button', name: 'Return to top' });
        });
      });
    });
  });
});
